/**
 * پنل اشکال‌زدایی توسعه‌دهنده.
 *
 * ⚠️ **تنها در حالت کارگاه رندر می‌شود.** در نسخه‌ی مشتری، `ConsultationPage`
 * این تب را نمی‌سازد. حتی اگر می‌ساخت، هیچ رازی اینجا نیست: کلید و رمز هرگز در
 * `ConsultationState` نمی‌آیند و لایه‌ی API آن‌ها را پاک می‌کند.
 *
 * محتوایش همان چیزی است که برای تشخیص لازم است: وزن‌های بازیابی، اجزای امتیاز
 * هر شاهد، slug مدل حل‌شده، provider مقصد، مصرف توکن و نسخه‌ها.
 */

import { Bug } from 'lucide-react'

import {
  Card,
  DetailRows,
  EmptyState,
  SectionHeader,
} from '@/components/ui/primitives'
import { fa, faCount, faScore, orDash } from '@/lib/format'
import { useMeta } from '@/lib/meta-context'

import type { ConsultationState } from '@/lib/types'

export const DebugPanel = ({ state }: { state: ConsultationState }) => {
  const { meta } = useMeta()
  const retrieval = state.retrieval_meta
  const usage = state.usage

  return (
    <div className="space-y-4">
      <Card>
        <SectionHeader title="مدل و نسخه" icon={<Bug className="size-4" />} />
        <div className="px-4 py-2">
          <DetailRows
            rows={[
              ['مدل چت', meta.chat_model],
              ['مدل embedding', meta.embedding_model],
              ['مدل حل‌شده', orDash(usage?.resolved_model)],
              ['provider مقصد', orDash(usage?.downstream_provider)],
              ['نسخه‌ی prompt', meta.prompt_version],
              ['نسخه‌ی دیتاست', orDash(retrieval?.dataset_version)],
              ['نسخه‌ی ایندکس', orDash(retrieval?.index_version)],
              ['نسخه‌ی ایندکس تکمیلی', orDash(retrieval?.supplementary_index_version)],
              ['شناسه‌ی مشاوره', state.consultation_id],
            ]}
          />
        </div>
      </Card>

      {usage !== null && (
        <Card>
          <SectionHeader title="مصرف" />
          <div className="px-4 py-2">
            <DetailRows
              rows={[
                ['تعداد درخواست', fa(usage.requests)],
                ['توکن ورودی', usage.input_tokens === null ? '—' : faCount(usage.input_tokens)],
                [
                  'توکن خروجی',
                  usage.output_tokens === null ? '—' : faCount(usage.output_tokens),
                ],
                ['توکن کل', usage.total_tokens === null ? '—' : faCount(usage.total_tokens)],
                ['هزینه', usage.cost === null ? '—' : `${fa(usage.cost, { maximumFractionDigits: 6 })} دلار`],
              ]}
            />
          </div>
        </Card>
      )}

      {retrieval !== null && (
        <Card>
          <SectionHeader title="بازیابی" />
          <div className="px-4 py-2">
            <DetailRows
              rows={[
                ['وزن معنایی', fa(retrieval.semantic_weight, { maximumFractionDigits: 2 })],
                ['وزن واژگانی', fa(retrieval.lexical_weight, { maximumFractionDigits: 2 })],
                ['وزن متادیتا', fa(retrieval.metadata_weight, { maximumFractionDigits: 2 })],
                [
                  'وزن تاریخی/گرافی',
                  fa(retrieval.historical_graph_weight, { maximumFractionDigits: 2 }),
                ],
                ['نامزد', fa(retrieval.candidate_count)],
                ['بازگشتی', fa(retrieval.returned_count)],
                ['بسط‌یافته', fa(retrieval.expanded_count)],
                ['نامزد تکمیلی', fa(retrieval.supplementary_candidate_count)],
                ['اصل تکمیلی', fa(retrieval.supplementary_principle_count)],
                ['بخش سند تکمیلی', fa(retrieval.supplementary_section_count)],
              ]}
            />
          </div>
          <div className="border-t border-line p-4">
            <p className="text-xs font-medium text-ink-muted">متن پرس‌وجو</p>
            <pre className="scrollbar-thin mt-1.5 max-h-40 overflow-auto rounded-sm bg-surface-sunken p-2 text-xs whitespace-pre-wrap">
              {retrieval.query_text}
            </pre>
          </div>
        </Card>
      )}

      {state.evidence.length > 0 && (
        <Card>
          <SectionHeader title="اجزای امتیاز هر شاهد" />
          <div className="scrollbar-thin overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-line bg-surface-sunken text-ink-muted">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">شناسه</th>
                  <th className="px-3 py-2 text-start font-medium">معنایی</th>
                  <th className="px-3 py-2 text-start font-medium">واژگانی</th>
                  <th className="px-3 py-2 text-start font-medium">متادیتا</th>
                  <th className="px-3 py-2 text-start font-medium">تاریخی</th>
                  <th className="px-3 py-2 text-start font-medium">نهایی</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {state.evidence.map((item) => (
                  <tr key={item.decision.decision_id}>
                    <td className="tabular px-3 py-1.5">{item.decision.decision_id}</td>
                    <td className="px-3 py-1.5 tabular-nums">{faScore(item.scores.semantic)}</td>
                    <td className="px-3 py-1.5 tabular-nums">{faScore(item.scores.lexical)}</td>
                    <td className="px-3 py-1.5 tabular-nums">{faScore(item.scores.metadata)}</td>
                    <td className="px-3 py-1.5 tabular-nums">
                      {faScore(item.scores.historical_graph)}
                    </td>
                    <td className="px-3 py-1.5 font-medium tabular-nums">
                      {faScore(item.scores.final_score)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card>
        <SectionHeader
          title="خروجی ساختاریافته‌ی خام"
          caption="همان چیزی که مدل برگردانده و با `DecisionAdvice` اعتبارسنجی شده."
        />
        {state.advice === null ? (
          <EmptyState title="توصیه‌ای ساخته نشد" />
        ) : (
          <pre className="scrollbar-thin max-h-96 overflow-auto p-4 text-xs" dir="ltr">
            {JSON.stringify(state.advice, null, 2)}
          </pre>
        )}
      </Card>

      {state.advisor_prompt !== '' && (
        <Card>
          <SectionHeader
            title="prompt مشاور"
            caption="زنجیره‌ی استدلال خام مدل ذخیره نمی‌شود؛ این متن ورودی است، نه فکر مدل."
          />
          <pre className="scrollbar-thin max-h-96 overflow-auto p-4 text-xs whitespace-pre-wrap">
            {state.advisor_prompt}
          </pre>
        </Card>
      )}
    </div>
  )
}
